import type { TextElement } from '../../shared/document';

function wrapLines(context: CanvasRenderingContext2D, text: string, maxWidth: number, letterSpacing: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      const width = context.measureText(candidate).width + Math.max(0, Array.from(candidate).length - 1) * letterSpacing;
      if (line && width > maxWidth) {
        lines.push(line);
        line = word;
      } else line = candidate;
    }
    lines.push(line);
  }
  return lines;
}

export function createTextTexture(element: TextElement, family: string, pixelRatio = 2): HTMLCanvasElement {
  const width = Math.max(1, element.transform.width);
  const height = Math.max(1, element.transform.height);
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(width * pixelRatio));
  canvas.height = Math.max(1, Math.ceil(height * pixelRatio));
  const context = canvas.getContext('2d');
  if (!context) return canvas;
  context.scale(pixelRatio, pixelRatio);
  const drawFace = (fill: string, offset: number): void => {
    context.save();
    context.translate(offset, offset);
    context.beginPath();
    context.rect(0, 0, width, height);
    context.clip();
    context.translate(width / 2, height / 2);
    context.transform(1, element.slant, 0, 1, 0, 0);
    context.translate(-width / 2, -height / 2);
    context.font = `${element.italic ? 'italic ' : ''}${element.fontWeight} ${element.fontSize}px "${family.replace(/["\\]/g, '')}"`;
    context.fillStyle = fill;
    context.textBaseline = 'middle';
    context.letterSpacing = `${element.letterSpacing}px`;
    const lines = wrapLines(context, element.text, width, element.letterSpacing);
    const lineHeight = element.fontSize * element.lineHeight;
    const curveHeight = Math.abs(element.curve);
    const textBlockHeight = (lines.length - 1) * lineHeight + element.fontSize + curveHeight;
    const verticalOffset = element.curve === 0
      ? height / 2 - (lines.length - 1) * lineHeight / 2 - element.fontSize / 2
      : (height - textBlockHeight) / 2 - Math.min(0, element.curve);
    for (const [lineIndex, line] of lines.entries()) {
      const characters = Array.from(line);
      const advances = characters.map((character) => Math.max(0, context.measureText(character).width));
      const total = advances.reduce((sum, advance) => sum + advance, 0) + Math.max(0, characters.length - 1) * element.letterSpacing;
      let x = element.align === 'left' ? 0 : element.align === 'center' ? (width - total) / 2 : width - total;
      const y = verticalOffset + lineIndex * lineHeight + element.fontSize / 2;
      characters.forEach((character, index) => {
        const advance = advances[index] ?? 0;
        const centerX = x + advance / 2;
        if (element.curve === 0 && element.letterSpacing === 0) {
          if (index === 0) {
            const drawX = element.align === 'left' ? 0 : element.align === 'center' ? width / 2 : width;
            context.textAlign = element.align;
            context.fillText(line, drawX, y, width);
          }
        } else {
          const normalizedX = Math.max(0, Math.min(1, centerX / width));
          const bend = element.curve * Math.sin(Math.PI * normalizedX);
          const tangent = element.curve * Math.PI / width * Math.cos(Math.PI * normalizedX);
          context.save();
          context.translate(centerX, y + bend);
          context.rotate(tangent);
          context.textAlign = 'center';
          context.fillText(character, 0, 0);
          context.restore();
        }
        x += advance + element.letterSpacing;
      });
    }
    context.restore();
  };
  for (let step = Math.ceil(element.depth); step > 0; step--) drawFace(element.depthColor, step);
  drawFace(element.fill, 0);
  return canvas;
}
